import type {ReactElement} from 'react';
import React from 'react';
import {Text, TextSize} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import {AnimationPanelType, PreviewType} from './default-animation-options.types';
import {AnimationType} from '@PosterWhiteboard/classes/item-animation.class';
import {getAssetUrl} from '@Utils/s3.util';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import styles from './default-animation-option.module.scss';

export interface DefaultAnimationOptionProps {
  key: string;
  onAnimationTypeClick: (type: AnimationPanelType) => void;
  introAnimation: AnimationType;
  outroAnimation: AnimationType;
  introPreviewType?: PreviewType;
  outroPreviewType?: PreviewType;
}

export function DefaultAnimationOption({introPreviewType = PreviewType.DESIGN, outroPreviewType = PreviewType.DESIGN, ...props}: DefaultAnimationOptionProps): ReactElement | null {
  const getDottedButton = (type: AnimationPanelType): ReactElement => {
    return (
      <ClickableDiv
        className={`flex-center radius-8 ${styles.dottedBtn}`}
        onClick={(): void => {
          props.onAnimationTypeClick(type);
        }}
      >
        <div className={`flex-center radius-8 hover-transition-all ${styles.iconContainer}`}>
          <Thumbnail
            key={type}
            data={{
              type: ThumbnailType.IMAGE,
              imageSrc: getAssetUrl(`poster-editor-thumbnails/animation_${type === AnimationPanelType.INTRO_ANIMATION_STYLES ? 'entrance' : 'exit'}.svg`),
            }}
          />
        </div>
      </ClickableDiv>
    );
  };

  const getAnimationThumbnail = (animation: AnimationType, type: AnimationPanelType): ReactElement => {
    return (
      <ClickableDiv
        key={type + animation}
        className={`flex-column-align-center radius-8 _full-width _cursor-pointer ${styles.thumbnailContainer}`}
        onClick={(): void => {
          props.onAnimationTypeClick(type);
        }}
      >
        <Thumbnail
          key={animation}
          className={`radius-4 _full-width _full-height ${styles.thumbnail}`}
          data={{
            type: ThumbnailType.IMAGE,
            className: '_full-width _full-height',
            imageSrc: getAssetUrl(
              `poster-editor-thumbnails/${type === AnimationPanelType.INTRO_ANIMATION_STYLES ? introPreviewType : outroPreviewType}-animations/${animation}.png?ts=1744006311`
            ),
          }}
        />
      </ClickableDiv>
    );
  };

  const getAnimationOption = (text: string, type: AnimationPanelType, animation: AnimationType): ReactElement => {
    return (
      <div className={`flex-column-align-center spacing-p-l-1 spacing-p-r-1 _fit-height`}>
        {animation === AnimationType.NONE ? getDottedButton(type) : getAnimationThumbnail(animation, type)}
        <Text val={text} className={'spacing-m-t-1 content-sub-text'} size={TextSize.XXSMALL} />
      </div>
    );
  };

  const getIntroAnimationButton = (): ReactElement => {
    return getAnimationOption(window.i18next.t('pmwjs_start'), AnimationPanelType.INTRO_ANIMATION_STYLES, props.introAnimation);
  };

  const getOutroAnimationButton = (): ReactElement => {
    return getAnimationOption(window.i18next.t('pmwjs_end'), AnimationPanelType.OUTRO_ANIMATION_STYLES, props.outroAnimation);
  };

  return (
    <li className="spacing-p-0 flex flex-h-row flex-items-center justify-content-between" key={props.key}>
      <Text val={window.i18next.t('pmwjs_animation')} size={TextSize.XSMALL} className="content-sub-text" />
      <div className={`flexbox ${styles.container}`}>
        {getIntroAnimationButton()}
        {getOutroAnimationButton()}
      </div>
    </li>
  );
}
